import { useLocation, URLSearchParamsInit, useSearchParams } from "react-router-dom";
import { useMemo, useState } from "react";

/**
 * 返回页面url中，指定键的参数值
 * 例如輸入 test 則返回 URL   http://localhost:3000/overview?test=1234
 * 則返回 1234 
 * 這個函式是一個自定義 React Hook，用於從頁面的 URL 中獲取指定鍵的參數值。
 */
export const useUrlQueryParam = <K extends string>(keys: K[]) => {
  const [searchParams] = useSearchParams();
  const setSearchParams = useSetUrlSearchParam();
  const [stateKeys] = useState(keys);
  return [
    useMemo(
      () =>
        subset(Object.fromEntries(searchParams), stateKeys) as {
          [key in K]: string;
        },
      [searchParams, stateKeys]
    ),
    (params: Partial<{ [key in K]: unknown }>) => {
      return setSearchParams(params);
      // iterator
      // iterator: https://codesandbox.io/s/upbeat-wood-bum3j?file=/src/index.js
    },


  ] as const;

};

/**
 * 返回 URL 參數值
 * 這個函式用於從當前頁面的 URL 中取得指定名稱的參數值。
 */
export function GetUrlParam(value: string) {
  const { search } = useLocation();
  const parameters = new URLSearchParams(search);
  const urlParam = parameters.get(`${value}`);
  return urlParam;
}

/**
 * 返回 URL Path (minus domain name)
 * 這個函式用於獲取當前頁面 URL 的路徑部分（不包括域名部分）。
 */
export function GetUrlPath() {
  const { pathname } = useLocation();
  return pathname;
}

// 這個函式用於從一個物件中篩選出指定的鍵和相應的值，返回一個新的物件。

export const subset = <O extends { [key in string]: unknown }, K extends keyof O>(obj: O, keys: K[]) => {
  const filteredEntries = Object.entries(obj).filter(([key]) => keys.includes(key as K));
  return Object.fromEntries(filteredEntries) as Pick<O, K>;
};

// 這個函式是一個自定義 Hook，用於設置 URL 的搜索參數。
export const useSetUrlSearchParam = () => {
  const [searchParams, setSearchParam] = useSearchParams();
  return (params: { [key in string]: unknown }) => {
    const o = cleanObject({
      ...Object.fromEntries(searchParams),
      ...params,
    }) as URLSearchParamsInit;
    return setSearchParam(o);
  };
};

// 這個函式用於從一個物件中刪除值為 undefined、null 或空字串的屬性。
export const cleanObject = (object?: { [key: string]: unknown }) => {
  // Object.assign({}, object)
  if (!object) {
    return {};
  }
  const result = { ...object };
  Object.keys(result).forEach((key) => {
    const value = result[key];
    if (isVoid(value)) {
      delete result[key];
    }
  });
  return result;
};
// 這個函式用於檢查一個值是否為 undefined、null 或空字串，如果是則返回 true，否則返回 false。
export const isVoid = (value: unknown) => value === undefined || value === null || value === "";
